import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Pusher from 'pusher-js';
import Seo from '../components/Seo';
import RequireSuperuser from '../components/RequireSuperuser';
import { AuthContext } from '../contexts/AuthContext';
import { fetchBlogJob } from '../services/blogApi';
import './BlogGenerating.css';

const STATUS_LINES = [
  'Locking in the angle from your starting direction…',
  'Drafting a clear opening — no throat-clearing…',
  'Building H2s that answer the real question…',
  'Filling each section with specific, useful detail…',
  'Cutting filler, hedging, and empty transitions…',
  'Checking product claims against the brief…',
  'Tightening the CTA so it earns the click…',
  'Writing SEO title and meta that match the post…',
  'One last pass for voice, then packaging the draft…',
];

function BlogGeneratingInner() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [status, setStatus] = useState('queued');
  const [error, setError] = useState(null);
  const [lineIdx, setLineIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const channelName = useMemo(
    () => (user?.id ? `user-${user.id}-blog` : null),
    [user?.id]
  );

  // Rotate funny status lines
  useEffect(() => {
    const t = setInterval(() => {
      setLineIdx((i) => (i + 1) % STATUS_LINES.length);
    }, 3200);
    return () => clearInterval(t);
  }, []);

  // Elapsed timer
  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const goComplete = (postId) => {
    if (!postId) return;
    navigate(`/blog/edit/${postId}`, { replace: true });
  };

  // Pusher: progress when backend finishes polling Anthropic
  useEffect(() => {
    if (!channelName || !jobId) return;
    const key = process.env.REACT_APP_PUSHER_KEY;
    const cluster = process.env.REACT_APP_PUSHER_CLUSTER;
    if (!key || !cluster) return undefined;

    const pusher = new Pusher(key, { cluster, enabledTransports: ['ws', 'wss'] });
    const channel = pusher.subscribe(channelName);

    const onComplete = (data) => {
      if (String(data?.jobId) !== String(jobId)) return;
      if (data.status === 'completed' && data.postId) {
        goComplete(data.postId);
      } else if (data.status === 'failed') {
        setStatus('failed');
        setError(data.error || 'Generation failed');
      }
    };
    const onFailed = (data) => {
      if (String(data?.jobId) !== String(jobId)) return;
      setStatus('failed');
      setError(data.error || 'Generation failed');
    };

    channel.bind('blog-job-complete', onComplete);
    channel.bind('blog-job-failed', onFailed);

    return () => {
      channel.unbind('blog-job-complete', onComplete);
      channel.unbind('blog-job-failed', onFailed);
      pusher.unsubscribe(channelName);
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, jobId]);

  // HTTP poll fallback (also covers missed Pusher)
  useEffect(() => {
    if (!user?.token || !jobId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const data = await fetchBlogJob(jobId, user.token);
        if (cancelled) return;
        const job = data.job;
        setStatus(job.status);
        if (job.status === 'completed' && job.postId) {
          goComplete(job.postId);
        } else if (job.status === 'failed') {
          setError(job.error || 'Generation failed');
        }
      } catch (err) {
        if (!cancelled) console.warn('[BlogGenerating] poll error', err);
      }
    };

    tick();
    const t = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, user?.token]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="blog-gen-screen" role="status" aria-live="polite">
      <Seo title="Drafting… | PrecisionTrader" noindex canonicalPath={`/blog/generating/${jobId}`} />

      <div className="blog-gen-glow" aria-hidden />
      <div className="blog-gen-grid" aria-hidden />

      <svg className="blog-gen-chart" viewBox="0 0 320 120" aria-hidden>
        <path
          className="blog-gen-chart-line"
          d="M0 90 C40 88, 50 40, 80 55 S120 100, 150 70 S200 20, 240 45 S280 95, 320 60"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle className="blog-gen-chart-dot" cx="240" cy="45" r="5" />
      </svg>

      <p className="blog-gen-kicker">PrecisionTrader · Blog forge</p>
      <h1 className="blog-gen-title">Drafting in progress</h1>
      <p className="blog-gen-line">{STATUS_LINES[lineIdx]}</p>

      <div className="blog-gen-bar" aria-hidden>
        <span className="blog-gen-bar-fill" />
      </div>

      <p className="blog-gen-meta">
        {status === 'failed' ? 'Stopped' : `Working · ${minutes}:${seconds}`}
        {status && status !== 'failed' ? ` · ${status}` : ''}
      </p>

      {error && (
        <div className="blog-gen-error">
          <p>{error}</p>
          <Link to="/blog">Back to blog</Link>
        </div>
      )}

      {!error && (
        <p className="blog-gen-hint">
          This usually takes a couple of minutes. Hang tight — we&apos;ll open the edit page when it&apos;s ready.
        </p>
      )}
    </div>
  );
}

/** Superuser-only full-screen loading experience while a blog job runs. */
export default function BlogGenerating() {
  return (
    <RequireSuperuser>
      <BlogGeneratingInner />
    </RequireSuperuser>
  );
}
